import { useEffect, useState } from 'react'
import { useBetStore } from 'src/store/bet'
import { stageTwoAt } from 'src/utils/betLock'

/**
 * Stage 2 of a hard round, as one line the board draws (hard close rev 4,
 * DESIGN §6): from the lock, while any bet it sent is still unanswered,
 * "placing"; from the board's estimate of K (lock + X) on, "confirming with
 * your casino", until the casino's answer comes (✓, or "not in time, stake
 * returning", which is the rejection's own toast). Null: nothing in flight,
 * or not a hard round's send.
 */
export type StageTwo = 'placing' | 'confirming' | null

export function useStageTwo(input: {
  /** When the board locked, `performance.now()` ms; undefined: no hard lock this round. */
  lockedAt: number | undefined
  lockLeadS: number
  /** The bets the lock sent. */
  betIds: readonly string[]
}): StageTwo {
  const { lockedAt, lockLeadS, betIds } = input
  const [pending, setPending] = useState(false)
  const [confirmingFor, setConfirmingFor] = useState<number | undefined>(undefined)

  // Followed only while the lock has bets out: a board that sent none
  // subscribes to nothing.
  useEffect(() => {
    if (betIds.length === 0) {
      setPending(false)
      return undefined
    }
    const read = () => {
      const unanswered = useBetStore.getState().unacceptedBets
      return betIds.some((id) => unanswered[id] !== undefined)
    }
    setPending(read())
    return useBetStore.subscribe(() => setPending(read()))
  }, [betIds])

  useEffect(() => {
    if (lockedAt === undefined || !pending) return undefined
    const { confirmingAt } = stageTwoAt(lockedAt, lockLeadS)
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now()
    const id = setTimeout(() => setConfirmingFor(lockedAt), Math.max(0, confirmingAt - now))
    return () => clearTimeout(id)
  }, [lockedAt, lockLeadS, pending])

  if (lockedAt === undefined || !pending) return null
  return confirmingFor === lockedAt ? 'confirming' : 'placing'
}
